import {
    browserSupportsWebAuthn,
    platformAuthenticatorIsAvailable,
} from '@simplewebauthn/browser';

let resolved: boolean | undefined;
let inFlight: Promise<boolean> | undefined;

/**
 * Whether this device has a user-verifying platform authenticator (Face ID /
 * Touch ID / Windows Hello), once known. `undefined` means detection has not
 * finished, which is not the same as "no" — treating it as one is what silently
 * suppressed the enrolment offer.
 */
export const getPasskeySupport = (): boolean | undefined => resolved;

/**
 * Detect platform-authenticator support, once per page. Memoised because the
 * answer cannot change within a page load and several surfaces ask.
 */
export const detectPasskeySupport = (): Promise<boolean> => {
    if (resolved !== undefined) return Promise.resolve(resolved);
    if (inFlight) return inFlight;

    if (typeof window === 'undefined' || !browserSupportsWebAuthn()) {
        resolved = false;
        return Promise.resolve(false);
    }

    inFlight = platformAuthenticatorIsAvailable()
        .then((available) => {
            resolved = available;
            return available;
        })
        .catch(() => {
            // Detection failure means no offer; never block the flow.
            resolved = false;
            return false;
        })
        .finally(() => {
            inFlight = undefined;
        });

    return inFlight;
};

// Support is a property of the device, not of any component, so start detecting
// as soon as this module loads rather than when a gate happens to mount. A gate
// that mounts and decides in the same tick would otherwise always read the
// pre-detection value.
if (typeof window !== 'undefined') {
    void detectPasskeySupport();
}
